import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouteMatch } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Form } from 'antd';

import { useCompany } from '@/core/domain/enterprise/Companies/Companies.hooks';
import {
  CiliaSubAreaNameEnum,
  CiliaSubAreas,
  WorkorderConfigurationCompanyFormValues,
} from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.types';
import { useWorkorderConfigurationCompanyQuery } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';

import { useUpdateBreadcrumbParam } from '@/contexts/BreadcrumbContextProvider';

import { PageProps } from '@/types/PageProps';
import CompanyWorkorderSettingsForm from '@/components/Companies/Settings/CompanyWorkorderSettingsForm';
import { useCompanyFormContext } from '@/components/Companies/CompaniesFormProvider';
import { SaveButton } from '@/components/_common';

function CompanyWorkorderSettingsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<WorkorderConfigurationCompanyFormValues>();
  const { companyId } = useRouteMatch<{ companyId: string }>().params;
  const {
    formValues,
    isLoading: contextLoading,
    handleSubmit,
    disabled,
  } = useCompanyFormContext();

  const { data: company } = useCompany(+companyId);
  useUpdateBreadcrumbParam(':companyId', company?.name);

  const defaultCiliaSubAreas = [
    {
      ciliaSubAreaName: CiliaSubAreaNameEnum.AUTO_BODY,
    },
    {
      ciliaSubAreaName: CiliaSubAreaNameEnum.GLAZING,
    },
    {
      ciliaSubAreaName: CiliaSubAreaNameEnum.TAPESTRY,
    },
    {
      ciliaSubAreaName: CiliaSubAreaNameEnum.ELECTRICAL,
    },
    {
      ciliaSubAreaName: CiliaSubAreaNameEnum.MECHANICAL,
    },
  ];

  const mergeCiliaSubAreasWithDefaults = (
    backendCiliaSubAreas?: CiliaSubAreas[],
  ) => {
    return defaultCiliaSubAreas.map((defaultItem) => {
      const backendItem = backendCiliaSubAreas?.find(
        (item) => item.ciliaSubAreaName === defaultItem.ciliaSubAreaName,
      );

      if (backendItem) return backendItem;

      return defaultItem;
    });
  };

  const { data: workorderConfig, isLoading } =
    useWorkorderConfigurationCompanyQuery(Number(companyId), !!companyId);

  useEffect(() => {
    if (workorderConfig) {
      form.setFieldsValue({
        ...workorderConfig,
        profitMargin: workorderConfig.profitMargin * 100,
        maxPartDiscount: workorderConfig.maxPartDiscount * 100,
        maxServiceDiscount: workorderConfig.maxServiceDiscount * 100,
        partPaint: workorderConfig.partPaint
          ? {
              ...workorderConfig.partPaint,
              costPerHour: workorderConfig.partPaint.costPerHour * 100,
            }
          : undefined,
        partRecuperation: workorderConfig.partRecuperation
          ? {
              ...workorderConfig.partRecuperation,
              costPerHour: workorderConfig.partRecuperation.costPerHour * 100,
            }
          : undefined,
        partChange: workorderConfig.partChange
          ? {
              ...workorderConfig.partChange,
              costPerHour: workorderConfig.partChange.costPerHour * 100,
            }
          : undefined,
        isInstallmentFree: workorderConfig.isInstallmentFree ? 'true' : 'false',

        ciliaSubAreas: mergeCiliaSubAreasWithDefaults(
          workorderConfig?.ciliaSubAreas,
        ),
      });
    } else {
      form.setFieldsValue({
        ciliaSubAreas: defaultCiliaSubAreas,
      });
    }
  }, [workorderConfig]);

  useEffect(() => {
    if (formValues.companyBudget) {
      form.setFieldsValue(formValues.companyBudget);
    }
  }, [formValues.companyBudget]);

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <div className="page">
        <CompanyWorkorderSettingsForm
          form={form}
          workorderConfig={workorderConfig}
          isLoading={formValues.companyBudget ? false : isLoading}
          extra={
            <SaveButton
              block
              onClick={handleSubmit}
              loading={contextLoading}
              disabled={disabled}
            >
              <FormattedMessage id="commons.entity.save" />
            </SaveButton>
          }
        />
      </div>
    </>
  );
}

export default CompanyWorkorderSettingsPage;
